import React from 'react';

interface Option {
  value: string;
  label: string;
}

interface FormFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function FormField({ 
  label, 
  error, 
  required = false, 
  children,
  className
}: FormFieldProps) {
  return (
    <div className={`space-y-1 ${className || ''}`}>
      <label className="block text-sm font-medium text-gray-200">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}

interface BaseInputProps {
  error?: string;
  className?: string;
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement>, BaseInputProps {}
interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement>, BaseInputProps {}
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement>, BaseInputProps {
  options: Option[];
}

export function Input({ className = '', error, ...props }: InputProps) {
  return (
    <input
      className={`
        w-full rounded-md bg-gray-800 border 
        ${error ? 'border-red-500' : 'border-gray-600'} 
        text-gray-200 px-3 py-2
        focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
        placeholder:text-gray-500
        ${className}
      `}
      {...props}
    />
  );
}

export function TextArea({ className = '', error, ...props }: TextAreaProps) {
  return (
    <textarea
      className={`
        w-full rounded-md bg-gray-800 border 
        ${error ? 'border-red-500' : 'border-gray-600'} 
        text-gray-200 px-3 py-2
        focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
        placeholder:text-gray-500
        ${className}
      `}
      {...props}
    />
  );
}

export function Select({ className = '', error, options, ...props }: SelectProps) {
  return (
    <select
      className={`
        w-full rounded-md bg-gray-800 border 
        ${error ? 'border-red-500' : 'border-gray-600'} 
        text-gray-200 px-3 py-2
        focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
        ${className}
      `}
      {...props}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}